import React from 'react';
import PropTypes from 'prop-types';
import AttributeGroupRow from '../../composites/AttributeGroupRow';
import { useTranslations } from '../../context/TranslationContext';
import Tooltip from '../../core/Tooltip';
import Icon from '../../core/Icon';

// Same component serves the skills bank, evaluation templates and the job ad; what a user may
// do per group depends on where it is mounted and whether the group has a save endpoint.
const ATTRIBUTE_SOURCE = { SKILLS_BANK: 'skills_bank', JOB_AD: 'job_ad', TEMPLATE: 'template' };
const EDITABLE_SOURCES = [ATTRIBUTE_SOURCE.TEMPLATE, ATTRIBUTE_SOURCE.JOB_AD];
const AI_MATCHING_GROUP_ID = 'skills';

const AiMatchingHint = () => {
    const { t } = useTranslations();
    return (
        <div className="ai-info-text">
            <span className="gradient-text-variant-2">
                {t('Base for AI Skills Matching')}
                <Tooltip title={t('The skills defined here will be automatically used for AI skills matching.')}>
                    <Icon value="help-circle" noMargin />
                </Tooltip>
            </span>
        </div>
    );
};

const AttributesManager = ({
    source = null,
    onChange = () => {},
    onRemoveAttribute,
    attributesConfig = [],
    attributesSet = {},
    loading = false,
    locale,
    readOnly = false,
    children,
    extraConfig = {},
}) => (
    <div className="skills-container">
        {attributesConfig.map((attributeConfig, index) => {
            const { id, title, description, url, save_url } = attributeConfig;
            const canRemove = EDITABLE_SOURCES.includes(source) || !!save_url;
            const canAdd = !!save_url;
            const canSearch = !readOnly || EDITABLE_SOURCES.includes(source) || (source === ATTRIBUTE_SOURCE.SKILLS_BANK && !!save_url);
            const extra = children && children(attributeConfig);

            return (
                <AttributeGroupRow
                    key={id}
                    title={title}
                    description={description}
                    meta={id === AI_MATCHING_GROUP_ID ? <AiMatchingHint /> : null}
                    attributes={attributesSet[id] || []}
                    divider={index < attributesConfig.length - 1}
                    removeTooltip={extraConfig?.remove_attribute_tooltip}
                    onRemove={canRemove && onRemoveAttribute ? (value) => onRemoveAttribute(id, value) : undefined}
                    onAdd={canSearch ? (value, result, cb) => {
                        onChange({ id, value, result, cb, save_url, locale });
                        if (typeof cb === 'function') cb();
                    } : undefined}
                    addUrl={url ? `${url}?lang=${locale}` : undefined}
                    addTags={canAdd}
                    addLoading={loading}
                    addId={`add-attribute-${id}`}
                >
                    {extra || null}
                </AttributeGroupRow>
            );
        })}
    </div>
);

AttributesManager.propTypes = {
    /** Where the manager is mounted: skills_bank, job_ad or template */
    source: PropTypes.oneOf(Object.values(ATTRIBUTE_SOURCE)),
    /** `({ id, value, result, cb, save_url, locale })` when an attribute is picked or created */
    onChange: PropTypes.func,
    /** `(groupId, value)` */
    onRemoveAttribute: PropTypes.func,
    /** Groups: `{ id, title, description, url, save_url }` */
    attributesConfig: PropTypes.array,
    /** Chosen attributes per group id */
    attributesSet: PropTypes.object,
    /** A pick is being saved */
    loading: PropTypes.bool,
    /** Search language */
    locale: PropTypes.string,
    /** Hide the add search where the source does not allow it */
    readOnly: PropTypes.bool,
    /** Render prop per group for content under the chips */
    children: PropTypes.func,
    /** `{ remove_attribute_tooltip }` */
    extraConfig: PropTypes.object,
};

export { ATTRIBUTE_SOURCE };
export default AttributesManager;
